/**
 * Shared utility functions for EDO permission checks
 * Implements the same logic as backend: admin/owner bypass and no-accesses = full access
 */

import userAccountSignal from '../signals/UserAccount.signal';
import { canPerformAction, checkProgramPermissions } from './programAccessPermissions';

/**
 * Check if user is admin/owner/superAdmin
 * @param {Object} userData - User data from userAccountSignal
 * @returns {boolean}
 */
export const isEdoAdminOrOwner = (userData) => {
  if (!userData) return false;
  const accountRole = userData?.role;
  return (
    accountRole === 'ADMIN' ||
    accountRole === 'OWNER' ||
    accountRole === 'SUPER_ADMIN'
  );
};

/**
 * Account Admin / Super Admin only (excludes OWNER).
 * Used for Change in Terms and other Admin-only Application Details edits.
 */
export const isEdoAdmin = (userData) => {
  if (!userData) return false;
  const accountRole = userData?.role;
  return accountRole === 'ADMIN' || accountRole === 'SUPER_ADMIN';
};

/**
 * Same role allow-list as backend `BATCH_DOCUMENT_UPLOAD_PROGRAM_ACCESS_ROLES` (loan processing batch documents).
 */
const BATCH_DOCUMENT_UPLOAD_PROGRAM_ROLES = ['ANALYST', 'STAFF', 'ADMIN', 'MANAGER'];

function programRoleForBatch(userData, batch) {
  if (!userData || batch?.fundingProgramId == null) return null;
  const access = (userData.edoUserProgramAccesses || []).find(
    (a) => a.fundingProgramId === batch.fundingProgramId
  );
  return access?.role ?? null;
}

/**
 * Queue step attachments (L1 / L2 / Outgoing): upload/replace matches backend assertCanUploadBatchStepDocument.
 */
export const canUploadEdoLoanBatchStepAttachments = (userData, batch) => {
  if (!userData || !batch || batch.status === 'COMPLETED') return false;
  if (isEdoAdminOrOwner(userData)) return true;
  const role = programRoleForBatch(userData, batch);
  return role ? BATCH_DOCUMENT_UPLOAD_PROGRAM_ROLES.includes(role) : false;
};

/**
 * Funding memo upload (draft or PENDING_L2): matches backend uploadFundingMemoWithConfig.
 */
export const canUploadEdoLoanBatchFundingMemo = (userData, batch) => {
  if (!userData || !batch) return false;
  if (batch.status !== 'DRAFT' && batch.status !== 'PENDING_L2') return false;
  if (isEdoAdminOrOwner(userData)) return true;
  const role = programRoleForBatch(userData, batch);
  return role ? BATCH_DOCUMENT_UPLOAD_PROGRAM_ROLES.includes(role) : false;
};

/** Replace/delete step attachment: same pool as upload while batch is not completed. */
export const canDeleteEdoLoanBatchStepAttachment = (userData, batch) =>
  batch?.status !== 'COMPLETED' && canUploadEdoLoanBatchStepAttachments(userData, batch);

/**
 * Level 1 batch queue (management_l1): backend canActAsManagerL1 — org admin/owner or program ADMIN/MANAGER.
 */
export const canSeeEdoLoanBatchManagementL1Queue = (userData) => {
  if (!userData) return false;
  if (isEdoAdminOrOwner(userData)) return true;
  return (userData.edoUserProgramAccesses || []).some(
    (a) => a.role === 'MANAGER' || a.role === 'ADMIN'
  );
};

/**
 * Level 2 batch queue (management_l2): backend canActAsL2Workflow — org admin/owner or program MANAGER only.
 */
export const canSeeEdoLoanBatchManagementL2Queue = (userData) => {
  if (!userData) return false;
  if (isEdoAdminOrOwner(userData)) return true;
  return (userData.edoUserProgramAccesses || []).some((a) => a.role === 'MANAGER');
};

/**
 * Reset Batch (all applications): blocked for program Analyst/Staff while status is PENDING_L1 (matches backend denyBatch).
 * @param {{ edoUserProgramAccesses?: Array<{ fundingProgramId: number; role: string }> }} userData
 * @param {{ status?: string; fundingProgramId?: number }} batch
 */
export const canResetEntireLoanBatch = (userData, batch) => {
  if (!userData || !batch) return false;
  if (batch.status === 'COMPLETED') return false;
  if (isEdoAdminOrOwner(userData)) return true;
  if (shouldHaveFullEdoAccess(userData)) return true;

  const fpId = batch.fundingProgramId;
  if (fpId == null) return false;
  const access = (userData.edoUserProgramAccesses || []).find((a) => a.fundingProgramId === fpId);
  if (!access) return false;

  const elevated = access.role === 'ADMIN' || access.role === 'MANAGER';
  if (elevated) return true;
  if (batch.status === 'PENDING_L1' && (access.role === 'ANALYST' || access.role === 'STAFF')) {
    return false;
  }
  return access.role === 'ANALYST' || access.role === 'STAFF';
};

/**
 * Check if user has any program or lender accesses configured
 * @param {Object} userData - User data from userAccountSignal
 * @returns {boolean}
 */
export const hasEdoAccessRestrictions = (userData) => {
  if (!userData) return false;
  const edoUserProgramAccesses = userData?.edoUserProgramAccesses || [];
  const edoUserLenderAccesses = userData?.edoUserLenderAccesses || [];
  return edoUserProgramAccesses.length > 0 || edoUserLenderAccesses.length > 0;
};

/**
 * Check if user should have full access (admin/owner OR no access restrictions)
 * @param {Object} userData - User data from userAccountSignal
 * @returns {boolean}
 */
export const shouldHaveFullEdoAccess = (userData) => {
  if (!userData) return false;
  return isEdoAdminOrOwner(userData) || !hasEdoAccessRestrictions(userData);
};

/**
 * Check permissions for a specific program
 * Returns full permissions if user is admin/owner or has no access restrictions
 * Otherwise checks permissions for the specific program
 * @param {number} fundingProgramId - The funding program ID to check
 * @param {Array<string>} permissions - Array of permissions to check (e.g., ['canView', 'canUpdate'])
 * @returns {Promise<Object>} - Object with permission flags
 */
export const checkEdoProgramPermissions = async (
  fundingProgramId,
  permissions = ['canView']
) => {
  const userData = userAccountSignal.value.userData;

  // Wait for userData to be loaded
  if (!userData) {
    return permissions.reduce((acc, perm) => ({ ...acc, [perm]: false }), {});
  }

  // If user should have full access, grant all permissions
  if (shouldHaveFullEdoAccess(userData)) {
    return permissions.reduce((acc, perm) => ({ ...acc, [perm]: true }), {});
  }

  // Fetch permissions once (API returns all permissions in one call)
  const response = await checkProgramPermissions(fundingProgramId);

  return permissions.reduce(
    (acc, perm) => ({
      ...acc,
      [perm]: response?.permissions?.[perm] ?? false,
    }),
    {}
  );
};

/**
 * Check permissions across all programs for an EDO
 * Returns full permissions if user is admin/owner or has no access restrictions
 * Otherwise checks if user has permission on ANY program
 * @param {Array<Object>} fundingPrograms - Array of funding program objects
 * @param {Array<string>} permissions - Array of permissions to check (e.g., ['canView', 'canInsert'])
 * @returns {Promise<Object>} - Object with permission flags
 */
export const checkEdoPermissionsAcrossPrograms = async (
  fundingPrograms = [],
  permissions = ['canView']
) => {
  const userData = userAccountSignal.value.userData;

  // Wait for userData to be loaded
  if (!userData) {
    return permissions.reduce((acc, perm) => ({ ...acc, [perm]: false }), {});
  }

  // If user should have full access, grant all permissions
  if (shouldHaveFullEdoAccess(userData)) {
    return permissions.reduce((acc, perm) => ({ ...acc, [perm]: true }), {});
  }

  // If no programs loaded yet: restricted users with MANAGER/ADMIN get canView to avoid
  // brief "Access Denied" flash; others are denied until programs load
  if (!fundingPrograms || fundingPrograms.length === 0) {
    const accesses = userData?.edoUserProgramAccesses || [];
    const hasManagerOrAdminAccess = accesses.some(
      (a) => a.role === 'ADMIN' || a.role === 'MANAGER'
    );
    if (hasManagerOrAdminAccess) {
      return permissions.reduce(
        (acc, perm) => ({ ...acc, [perm]: perm === 'canView' }),
        {}
      );
    }
    return permissions.reduce((acc, perm) => ({ ...acc, [perm]: false }), {});
  }

  // Fetch permissions once per program (API returns all permissions in one call)
  const programPermissionResponses = await Promise.all(
    fundingPrograms.map((program) => checkProgramPermissions(program.id))
  );

  // User has permission if they have it on ANY program
  return permissions.reduce(
    (acc, perm) => ({
      ...acc,
      [perm]: programPermissionResponses.some(
        (response) => response?.permissions?.[perm] === true
      ),
    }),
    {}
  );
};

/**
 * Check if user has access to a specific lender
 * Program Admin users can see lender info in applications, but need explicit lender access to see the "Lender Details" button
 * @param {Object} userData - User data from userAccountSignal
 * @param {number} lenderId - The lender ID to check access for
 * @returns {boolean} - True if user has access to the lender, false otherwise
 */
export const hasLenderAccess = (userData, lenderId) => {
  if (!userData || !lenderId) return false;
  if (userData?.account?.portalType !== 'EDO') return true; // Non-EDO users always have access
  
  // Type assertion: edoUserLenderAccesses exists at runtime but isn't in the User type
  const edoUserLenderAccesses = userData?.edoUserLenderAccesses || [];
  
  // User must have explicit lender access permissions to see the "Lender Details" button
  // Even Program Admin users need explicit lender access entries
  if (edoUserLenderAccesses.length === 0) return false;
  
  // Check if this lender is in the user's accessible lenders
  return edoUserLenderAccesses.some((access) => access.lenderId === lenderId);
};

